// Libraries
import * as yup from "yup";
import { DateTime } from "luxon";

// Utils
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhinModCheck from "Components/ModCheck/PhinModCheck";
import { isUnderOneYearOld } from "Common/Components/Formik/UnderOneYearOld";
import { isAllZeroPhin } from "Utils/Normalization";

// Regex
import {
  alphaNumericRegex,
  nameCharactersRegex,
} from "Validation/validationRegex";

const getRelaxedRequiredStringSchema = (message, relaxed) =>
  relaxed ? yup.string().notRequired() : yup.string().required(message);

const allowOnlyAlphaNumeric = (schema, t) =>
  yup
    .string()
    .matches(alphaNumericRegex, t("patientValidation.AlphaNumericOnlyAllowed"))
    .concat(schema);

const getHealthCardValidationSchema = (t, province) => {
  const disallowNonMbZeroPhin = (schema) =>
    schema.test("mb-zero-only", t("patientValidation.InvalidPHIN"), (value) => {
      if (!value) return true;
      return !/^0+$/.test(value);
    });

  const getNonMbHealthCardSchema = (schema) =>
    disallowNonMbZeroPhin(allowOnlyAlphaNumeric(schema, t));

  switch (province) {
    case "MB":
      return allowOnlyAlphaNumeric(
        yup
          .string()
          .required(t("patientValidation.PHINIsRequired"))
          .test(
            "phin-mod-check",
            t("patientValidation.InvalidPHIN"),
            function (phin) {
              if (!phin) return true;
              if (!alphaNumericRegex.test(phin)) return true;
              if (isAllZeroPhin(phin)) {
                const dob = this.parent?.dob;
                return (
                  isUnderOneYearOld(dob) ||
                  this.createError({
                    message: t("patientValidation.ZeroPHINOnlyForInfants"),
                  })
                );
              }
              return PhinModCheck(phin);
            }
          ),
        t
      );
    case "AB":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(9, t("patientValidation.ULIMustBe9DigitsLong"))
          .required(t("patientValidation.ULINumberIsRequired"))
      );
    case "SK":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(9, t("patientValidation.SKIMustBe9DigitsLong"))
          .required(t("patientValidation.SKINumberIsRequired"))
      );
    case "NT":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(8, t("patientValidation.NTMustBe8DigitsLong"))
          .required(t("patientValidation.NTIsRequired"))
      );
    case "YT":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(9, t("patientValidation.YTHealthMustBe9DigitsLong"))
          .required(t("patientValidation.YTHealthIsRequired"))
      );
    case "NU":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(9, t("patientValidation.NunavutMustBe9DigitsLong"))
          .required(t("patientValidation.NunavutIsRequired"))
      );
    case "BC":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(10, t("patientValidation.BCMustBe10DigitsLong"))
          .required(t("patientValidation.BCIsRequired"))
      );
    case "ON":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .min(10, t("patientValidation.OHIPMustBeAtLeast10DigitsLong"))
          .max(12, t("patientValidation.OHIPCannotExceed12DigitsLong"))
          .required(t("patientValidation.OHIPIsRequired"))
      );
    case "NB":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(9, t("patientValidation.NBHealthMustBe9DigitsLong"))
          .required(t("patientValidation.NBNumberIsRequired"))
      );
    case "NS":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(10, t("patientValidation.NSMustBe10DigitsLong"))
          .required(t("patientValidation.NSNumberIsRequired"))
      );
    case "PE":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(8, t("patientValidation.PEIMustBe8DigitsLong"))
          .required(t("patientValidation.PEINumberIsRequired"))
      );
    case "NL":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(12, t("patientValidation.NLMustBe12DigitsLong"))
          .required(t("patientValidation.NLNumberIsRequired"))
      );
    case "QC":
      return getNonMbHealthCardSchema(
        yup
          .string()
          .length(12, t("patientValidation.HealthInsuranceMustBe12DigitsLong"))
          .required(t("patientValidation.HealthInsuranceIsRequired"))
      );
    default:
      return yup
        .string()
        .test(
          "health-card-province-required",
          t("patientValidation.HealthCardProvinceIsRequired"),
          function (value) {
            if (!value) return true;

            return this.createError({
              path: "healthCardProvince",
              message: t("patientValidation.HealthCardProvinceIsRequired"),
            });
          }
        );
  }
};

const buildPatientValidationSchema = (t, relaxed = false) => {
  return yup
    .object()
    .shape({
      // Required Information
      firstName: yup
        .string()
        .matches(nameCharactersRegex, t("common.validation.nameCharacters"))
        .max(30, t("patientValidation.FirstNameCannotExceed30"))
        .required(t("patientValidation.FirstNameIsRequired")),

      lastName: yup
        .string()
        .matches(nameCharactersRegex, t("common.validation.nameCharacters"))
        .max(75, t("patientValidation.LastNameCannotExceed75"))
        .required(t("patientValidation.LastNameIsRequired")),

      dob: yup
        .string()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null ? undefined : value
        )
        .required(t("patientValidation.DateOfBirthIsRequired"))
        .test(
          "valid-date",
          t("patientValidation.PleaseEnterAValidDate"),
          (value) => {
            if (!value) return true;
            return DateTime.fromISO(value).isValid;
          }
        )
        .test(
          "min-date",
          t("patientValidation.DOBMustBeGreaterThan1900"),
          (value) => {
            if (!value) return true;
            return DateTime.fromISO(value) >= DateTime.fromISO("1900-01-01");
          }
        )
        .test(
          "max-date",
          t("patientValidation.DateCannotBeInTheFuture"),
          (value) => {
            if (!value) return true;
            return DateTime.fromISO(value) <= DateTime.now();
          }
        ),

      primaryPhone: yup
        .string()
        .nullable()
        .test(
          "is-valid-phone",
          t("patientValidation.InvalidPhoneNumber"),
          (value) => {
            if (!value) return true;
            const phoneNumber = parsePhoneNumberFromString(value, "CA");
            return phoneNumber ? phoneNumber.isValid() : false;
          }
        ),
      secondaryPhone: yup
        .string()
        .nullable()
        .test(
          "is-valid-phone",
          t("patientValidation.InvalidPhoneNumber"),
          (value) => {
            if (!value) return true;
            const phoneNumber = parsePhoneNumberFromString(value, "CA");
            return phoneNumber ? phoneNumber.isValid() : false;
          }
        ),
      noPhoneAcknowledgement: yup.boolean(),

      email: (relaxed
        ? yup.string().notRequired()
        : yup.string().required(t("patientValidation.EmailIsRequired"))
      )
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null ? undefined : value
        )
        .email(t("patientValidation.PleaseInsertAValidEmail"))
        .max(75, t("patientValidation.EmailCannotExceed75")),

      preferredLanguage: yup
        .string()
        .required(t("patientValidation.PreferredLanguageIsRequired")),

      healthCardProvince: yup
        .string()
        .required(t("patientValidation.HealthCardProvinceIsRequired")),

      registrationNumber: yup.string().when("healthCardProvince", {
        is: (value) => value === "MB",
        then: (schema) =>
          allowOnlyAlphaNumeric(
            schema
              .matches(
                /^(?!0{6})[A-Za-z0-9]{6}$/,
                t("patientValidation.RegistrationNumberMustBe6CharsLong")
              )
              .test(
                "registration-required",
                t("patientValidation.RegistrationNumberIsRequired"),
                function (value) {
                  if (value) return true;
                  const { dob, healthCardNumber } = this.parent || {};
                  const isInfantZeroPhin =
                    isUnderOneYearOld(dob) && isAllZeroPhin(healthCardNumber);
                  return this.createError({
                    message: t(
                      isInfantZeroPhin
                        ? "patientValidation.RegistrationNumberIsRequiredInfantZeroPhin"
                        : "patientValidation.RegistrationNumberIsRequired"
                    ),
                  });
                }
              ),
            t
          ),
        otherwise: (schema) => schema,
      }),

      healthCardNumber: yup
        .string()
        .when("healthCardProvince", (prov) =>
          getHealthCardValidationSchema(t, prov[0])
        ),

      // Address
      address: yup
        .string()
        .max(75, t("patientValidation.AddressCannotExceed75"))
        .concat(
          getRelaxedRequiredStringSchema(
            t("patientValidation.AddressIsRequired"),
            relaxed
          )
        ),

      city: yup
        .string()
        .max(30, t("patientValidation.CityCannotExceed30"))
        .concat(
          getRelaxedRequiredStringSchema(
            t("patientValidation.CityIsRequired"),
            relaxed
          )
        ),

      province: yup
        .string()
        .concat(
          getRelaxedRequiredStringSchema(
            t("patientValidation.ProvinceIsRequired"),
            relaxed
          )
        ),

      postalCode: yup
        .string()
        .matches(
          /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/,
          t("patientValidation.MustUseValidLettersFormat")
        )
        .concat(
          getRelaxedRequiredStringSchema(
            t("patientValidation.PostalCodeIsRequired"),
            relaxed
          )
        ),
    })
    .test(
      "phone-or-acknowledgement",
      t("patientValidation.PhoneNumberOrAcknowledgementRequired"),
      function (values) {
        if (relaxed) return true;
        if (!values) return true;
        if (typeof values.noPhoneAcknowledgement === "undefined") {
          return true;
        }
        const primary = (values.primaryPhone || "").trim();
        const secondary = (values.secondaryPhone || "").trim();
        const isValid = Boolean(
          primary || secondary || values.noPhoneAcknowledgement
        );
        if (isValid) return true;
        return this.createError({
          path: "noPhoneAcknowledgement",
          message: t("patientValidation.PhoneNumberOrAcknowledgementRequired"),
        });
      }
    );
};

export const patientValidationSchema = (t) => buildPatientValidationSchema(t);

export const guestPatientValidationSchema = (t) =>
  buildPatientValidationSchema(t, true);
